import type { FindingsPeriod, FindingsScope } from "@iamu/shared";
import { useParams, useSearchParams } from "react-router-dom";
import { lensColorMap } from "@/components/finding-badges";
import { STALE_TIMES } from "@/lib/query-performance";
import { trpc } from "@/lib/trpc";
import { formatBuildDisplayLabel } from "./period-options";
import {
  formatDay,
  parseScope,
  storeScope,
  viewerTimeZone,
  withScope,
} from "./scope-url-state";

export function periodPhrase(
  period: FindingsPeriod | undefined,
  buildLabel: string | null,
): string {
  if (!period) return "all time";
  if (period.kind === "last") return `in the last ${period.days} days`;
  if (period.kind === "sinceBuild") {
    return buildLabel ? `since build ${buildLabel}` : "since a build";
  }
  if (!period.until) return `since ${formatDay(period.since)}`;
  return period.since === period.until
    ? `on ${formatDay(period.since)}`
    : `from ${formatDay(period.since)} to ${formatDay(period.until)}`;
}

export const ALL = "all";

export const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
  { value: "nit", label: "Nit" },
] as const;
export type SeverityValue = (typeof SEVERITY_OPTIONS)[number]["value"];

export const LENS_OPTIONS = (
  ["persona", "domain_expert", "designer"] as const
).map((value) => ({ value, label: `${lensColorMap[value].label} lens` }));
export type LensValue = (typeof LENS_OPTIONS)[number]["value"];

export const MODE_HELP =
  "Still happening: seen in any run in the period. New: first seen in the period.";
export const COMMITTED_HELP =
  "Leave out runs taken against uncommitted changes (+wip builds).";

export interface ScopeProps {
  projectId: string;
  scope: FindingsScope;
  setScope: (next: FindingsScope) => void;
}

export const withPeriod = (
  scope: FindingsScope,
  period: FindingsPeriod | undefined,
): FindingsScope =>
  period
    ? { ...scope, period, tz: viewerTimeZone() }
    : { ...scope, period: undefined, periodMode: undefined, tz: undefined };

export function useFindingsScope() {
  const { id: projectId } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const scope = parseScope(params);
  const setScope = (next: FindingsScope) => {
    const p = withScope(params, next);
    if (projectId) storeScope(projectId, p);
    setParams(p, { replace: true });
  };
  return { scope, setScope };
}

export function useScopeBuilds(projectId: string, period?: FindingsPeriod) {
  const { data } = trpc.build.list.useQuery(
    { projectId, limit: 6 },
    { staleTime: STALE_TIMES.list },
  );
  const builds = data?.items ?? [];
  const selectedId = period?.kind === "sinceBuild" ? period.buildId : null;
  const selected = selectedId
    ? (builds.find((b) => b.id === selectedId) ?? null)
    : null;
  const formattedSelected = selected ? formatBuildDisplayLabel(selected) : null;
  const selectedLabel = formattedSelected
    ? formattedSelected.sha
      ? `${formattedSelected.primary} · ${formattedSelected.sha}`
      : formattedSelected.primary
    : null;
  return {
    builds,
    selectedLabel,
    environments: data?.environments ?? [],
    branches: data?.branches ?? [],
    hasUncommitted: data?.hasUncommitted ?? false,
  };
}

export function facetOptions(
  values: string[],
  current: string | undefined,
  label: (v: string) => string = (v) => v,
) {
  const all =
    current && !values.includes(current) ? [...values, current] : values;
  return all.map((value) => ({ value, label: label(value) }));
}

export function useScopeEntities(projectId: string) {
  const { data: personas } = trpc.persona.list.useQuery({ projectId });
  const { data: capabilities } = trpc.capability.list.useQuery({ projectId });
  return { personas: personas ?? [], capabilities: capabilities ?? [] };
}
